import * as React from 'react'

import { cn } from '../lib/utils'
import type { PlateRole } from './plate-role-chip'

type RoleValue = `${PlateRole}`

// Literal class strings — Tailwind's content scan can't see `bg-role-${role}`.
const ROLE_BG: Record<RoleValue, string> = {
  veggie: 'bg-role-veggie',
  protein: 'bg-role-protein',
  starch: 'bg-role-starch',
  sauce: 'bg-role-sauce'
}

export interface DotScaleProps {
  /** Current step, 0–`max`. */
  value: number
  /** Number of dots. Default 4. */
  max?: number
  onChange: (value: number) => void
  /** Fills the selected dots. Omit for the amber primary. */
  role?: RoleValue
  /**
   * Accessible name for the group, already translated — e.g. "How much protein". Without it the
   * scale is a row of unlabelled dots that assistive tech announces as nothing in particular.
   */
  label: string
  /** Names each dot, e.g. `(n) => t('scale.parts', { count: n })`. Defaults to the bare number. */
  dotLabel?: (step: number) => string
  /**
   * Appended to the CURRENT dot's accessible name, e.g. "3, activate to go down". Stepping down is
   * invisible without it — the gesture exists but nothing announces it. Also the dot's tooltip.
   */
  clearHint?: string
  /**
   * Dot diameter as any CSS length — a fixed size, or a fluid one like
   * `clamp(1.0625rem, 4.4cqi, 1.375rem)` to grow with a container. Default 17px.
   *
   * The hit box scales with it, so a bigger dot means a bigger touch target rather than the same
   * target behind a bigger mark.
   *
   * Omit it and the scale reads `--dot-scale-size` instead, which a caller can set from a class —
   * the way to make the size responsive, since an inline style can't carry a media query.
   */
  dotSize?: string
  className?: string
}

/** 17px, in rem so it follows the user's text size. Not `ch` — that tracks the resolved font. */
const DEFAULT_DOT = 'var(--dot-scale-size, 1.0625rem)'
// The hit box is the dot plus a gutter, in the 23:17 proportion the fixed-size scale was built on.
// Derived once here, so the dot and its target can't drift apart.
const HIT_BOX = 'calc(var(--dot) * (23 / 17))'
const GUTTER = 'calc((var(--dot-hit) - var(--dot)) / 2)'

/**
 * A row of dots for a small "how much of this" scale — 0 to `max`, no numbers shown.
 *
 * Activating dot *n* sets the value to *n*; activating the current highest dot steps back down to
 * *n − 1*, which is how the user reaches 0. Sibling of `Rating`: same discrete-scale interaction,
 * same radiogroup mechanics, a dot instead of a star.
 */
export function DotScale({ value, max = 4, onChange, role, label, dotLabel, clearHint, dotSize, className }: DotScaleProps) {
  const [hover, setHover] = React.useState(0)
  // Radios group by `name`, so each scale needs its own — otherwise two on one page would fight
  // over a single selection.
  const groupName = React.useId()
  const steps = Array.from({ length: max }, (_, i) => i + 1)
  const shown = hover || value

  // Real `<input type="radio">`s sharing a name, not buttons wearing `role="radio"`: the browser
  // supplies the whole APG radiogroup contract — arrow keys move and select, the group is a single
  // tab stop, focus enters on the checked dot. They're `sr-only`; the label draws the visible dot.
  return (
    <span
      role="radiogroup"
      aria-label={label}
      // The negative margin absorbs the outer half-gutter of the end hit boxes, so the dots sit
      // flush with whatever edge the scale is aligned to.
      style={{ '--dot': dotSize ?? DEFAULT_DOT, '--dot-hit': HIT_BOX, '--gutter': GUTTER } as React.CSSProperties}
      className={cn('-mx-[var(--gutter)] inline-flex items-center', className)}
      onMouseLeave={() => setHover(0)}>
      {steps.map((step) => (
        <label
          key={step}
          onMouseEnter={() => setHover(step)}
          title={value === step ? clearHint : undefined}
          // A dot is only as big a target as itself. The transparent box around it carries the
          // touch area out to 44px tall and a gutter wider than the dot, with no dead space
          // between neighbours — people use this one mid-cook.
          className="group flex h-11 w-[var(--dot-hit)] cursor-pointer items-center justify-center">
          {/* A radio group has no native deselect, so stepping down is re-activating the current
              dot — and that takes three handlers: `change` never fires for a repeat, `click` covers
              the pointer, and `keydown` covers Space/Enter, which dispatch neither on an
              already-checked radio. */}
          <input
            type="radio"
            name={groupName}
            value={step}
            checked={value === step}
            onClick={() => {
              if (value === step) onChange(step - 1)
            }}
            onKeyDown={(e) => {
              if (value === step && (e.key === ' ' || e.key === 'Enter')) {
                e.preventDefault()
                onChange(step - 1)
              }
            }}
            onChange={() => onChange(step)}
            className="sr-only"
          />
          <span className="sr-only">
            {dotLabel ? dotLabel(step) : String(step)}
            {value === step && clearHint ? `, ${clearHint}` : ''}
          </span>
          <span
            className={cn(
              'size-[var(--dot)] rounded-full transition-[transform,background-color] duration-200 group-hover:scale-[1.15]',
              'group-has-[:focus-visible]:ring-[3px] group-has-[:focus-visible]:ring-pelorous-500 group-has-[:focus-visible]:ring-offset-2 group-has-[:focus-visible]:ring-offset-background dark:group-has-[:focus-visible]:ring-pelorous-300',
              'motion-reduce:transition-none motion-reduce:group-hover:scale-100',
              shown >= step ? (role ? ROLE_BG[role] : 'bg-primary') : 'bg-track'
            )}
          />
        </label>
      ))}
    </span>
  )
}
